import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash } from "@phosphor-icons/react";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const r = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (r.ok) {
      const dest = location.state?.from?.pathname || "/";
      navigate(dest, { replace: true });
    } else {
      setError(r.error);
    }
  };

  return (
    <div
      className="min-h-screen grid lg:grid-cols-2 bg-background"
      data-testid="page-login"
    >
      <div className="hidden lg:flex relative bg-primary overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 30% 20%, rgba(255,255,255,0.06) 0, transparent 50%), radial-gradient(circle at 70% 80%, rgba(255,255,255,0.04) 0, transparent 50%)`,
          }}
        />
        <div className="relative z-10 p-16 flex flex-col justify-between text-primary-foreground w-full">
          <div className="flex items-center gap-4">
            <img
              src="icone-logo-up2.png"
              alt="UP2 Soluções"
              className="h-20 w-20 object-contain rounded-sm bg-white/5 backdrop-blur p-1"
              data-testid="login-brand-logo"
            />
            <div>
              <div className="text-base font-semibold tracking-tight leading-none">
                UP2 Soluções
              </div>
              <div className="text-[11px] uppercase tracking-[0.12em] opacity-60 mt-1">
                SEFAZ Worker · NF-e / NFC-e
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <h1 className="text-4xl tracking-tight font-semibold leading-tight">
              Sua gestão fiscal,
              <br />
              <span className="opacity-60">simples e sem complicação.</span>
            </h1>
            <p className="text-sm opacity-75 max-w-md leading-relaxed">
              Centralize todos os XMLs de NF-e e NFC-e em um só lugar, com
              segurança e organização automática. Compartilhe com seu contador
              de forma prática e tenha controle total das suas informações
              fiscais.
            </p>
            <p className="text-sm font-medium opacity-90 max-w-md leading-relaxed pt-2">
              Menos retrabalho. Mais controle. Total tranquilidade.
            </p>
            <div className="flex gap-6 pt-6 border-t border-white/10 mt-6">
              <Stat label="Modelo" value="55 / 65" />
              <Stat label="Evento" value="210210" />
              <Stat label="Delay" value="3s" />
              <Stat label="Cooldown" value="1h" />
            </div>
          </div>
          <div className="text-[11px] uppercase tracking-[0.08em] opacity-50">
            Painel restrito · acesso por convite · UP2 Soluções ©{" "}
            {new Date().getFullYear()}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form
          onSubmit={submit}
          className="w-full max-w-sm"
          data-testid="login-form"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img
              src="icone-logo-up2.png"
              alt="UP2 Soluções"
              className="h-16 w-16 object-contain rounded-sm bg-primary/10 p-0.5"
            />
            <div>
              <div className="text-sm font-semibold tracking-tight">
                UP2 Soluções
              </div>
              <div className="text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
                SEFAZ Worker · NF-e / NFC-e
              </div>
            </div>
          </div>
          <div className="label-overline mb-2">Acessar painel</div>
          <h2 className="text-2xl font-semibold tracking-tight mb-1">
            Entrar com sua conta
          </h2>
          <p className="text-sm text-muted-foreground mb-8">
            Use as credenciais fornecidas pelo administrador da plataforma.
          </p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                data-testid="login-email"
                placeholder="seu@email.com"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <div className="relative mt-1.5">
                <Input
                  id="password"
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {show ? <EyeSlash size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && (
              <div
                className="text-sm rounded-sm border border-destructive/30 bg-destructive/5 text-destructive px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-sm"
              data-testid="login-submit"
            >
              {loading ? "Entrando..." : "Entrar"}
            </Button>

            <p className="text-xs text-muted-foreground text-center pt-4">
              Não possui acesso? Solicite ao administrador da plataforma.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[11px] uppercase tracking-[0.08em] opacity-50">
      {label}
    </div>
    <div className="font-mono text-lg mt-1">{value}</div>
  </div>
);

export default Login;
